import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Download, Trash2, Play, Loader2, Archive, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function BackupsPage() {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/backups");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/backups/create");
      toast.success(`Yedek alındı · ${r.data.doc_count} kayıt · ${fmtBytes(r.data.size_bytes)}`);
      load();
    } catch { toast.error("Yedek alınamadı"); }
    finally { setRunning(false); }
  };

  const download = async (id, name) => {
    try {
      const r = await api.get(`/backups/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || `backup-${id.slice(0, 8)}.json.gz`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("İndirilemedi"); }
  };

  const del = async (id) => {
    if (!window.confirm("Bu yedeği silmek istediğinize emin misiniz?")) return;
    await api.delete(`/backups/${id}`);
    toast.success("Silindi");
    load();
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="backups-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Archive size={22} />
          <div>
            <div className="label-overline">Veritabanı Yedekleri</div>
            <h1 className="mt-1 font-display font-black text-3xl md:text-4xl tracking-tight">Yedekler</h1>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} className="h-10 px-4 text-sm border border-zinc-200 rounded-md hover:bg-zinc-100 flex items-center gap-2" data-testid="reload-backups-btn">
            <RefreshCw size={13} /> Yenile
          </button>
          <button onClick={createNow} disabled={running} className="h-10 px-5 text-sm bg-zinc-950 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2 font-medium" data-testid="create-backup-btn">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={13} />}
            {running ? "Alınıyor…" : "Şimdi Yedek Al"}
          </button>
        </div>
      </div>

      <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
        Her <b>Pazar 03:00</b>'da MongoDB koleksiyonlarının gzip'li yedeği otomatik alınır. 12 haftadan eski yedekler otomatik silinir. İhtiyaç halinde manuel yedek de alabilirsiniz.
      </p>

      <div className="mt-6 grid-card overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-zinc-50 text-left">
            <tr className="border-b border-zinc-200">
              <Th>Tarih</Th><Th>Kayıt Sayısı</Th><Th>Boyut</Th><Th>Koleksiyonlar</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => {
              const fn = (b.storage_path || "").split("/").pop();
              return (
                <tr key={b.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`backup-row-${b.id}`}>
                  <Td className="font-medium">{fmtDate(b.created_at)}</Td>
                  <Td className="tabular-nums">{b.doc_count}</Td>
                  <Td className="tabular-nums">{fmtBytes(b.size_bytes)}</Td>
                  <Td>
                    <div className="flex gap-1 flex-wrap text-[10px]">
                      {Object.entries(b.collections || {}).map(([k, v]) => (
                        <span key={k} className="px-1.5 py-0.5 bg-zinc-100 rounded">{k}:{v}</span>
                      ))}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => download(b.id, fn)} className="px-2 py-1 text-xs text-[#0033cc] hover:bg-blue-50 rounded flex items-center gap-1" data-testid={`dl-backup-${b.id}`}>
                        <Download size={12} /> İndir
                      </button>
                      <button onClick={() => del(b.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`del-backup-${b.id}`}><Trash2 size={13} /></button>
                    </div>
                  </Td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-sm text-zinc-500">Henüz yedek yok · "Şimdi Yedek Al" ile ilk yedeği oluşturun</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 label-overline font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
